import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { DndContext, PointerSensor, useSensor, useSensors, type DragEndEvent } from "@dnd-kit/core";
import { SortableContext, useSortable, verticalListSortingStrategy } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import type { Position, Player } from "../../shared/models/Player";
import type { RankingsListKey, TiersByPos } from "../../shared/utils/xlsxRankings";
import { formatTeamAbbreviation } from "../../shared/utils/teamAbbreviation";
import Board from "./Board";
import type { BoardTab, DraftStyle } from "./boardTypes";
import type { StarterConfig, TeamOverridesState } from "../../shared/components/TeamsBoard";
import PlayerProfileModal from "./PlayerProfileModal";
import TeamLogo from "../../shared/components/TeamLogo";
import { TierOverlay } from "../../shared/components/Rankings/TierBar";
import { playerProfilesById } from "../../shared/data/playerProfiles";
import { playerGameLogsById } from "../../shared/data/playerGameLogs";

const TOUCH_LAYOUT_TABS: BoardTab[] = ["Rankings Board", "Draft Board", "Draft Planner", "Cheatsheet", "Teams"];
const DEFAULT_CHEATSHEET_VISIBLE_POSITIONS: Record<Position, boolean> = {
  QB: true,
  RB: true,
  WR: true,
  TE: true,
  K: true,
  DST: true,
};
const MOBILE_SOURCE_TABS: Array<{ key: RankingsListKey; label: string }> = [
  { key: "Rankings", label: "My Rankings" },
  { key: "Experts", label: "Expert Rankings" },
];
const MOBILE_RANKINGS_ZOOM_LEVELS = [0.1, 0.25, 0.5, 0.75, 0.85, 1, 1.15] as const;
const MOBILE_BOARD_ZOOM_MIN = 0.75;
const MOBILE_BOARD_ZOOM_MAX = 1.5;
const MOBILE_BOARD_ZOOM_STEP = 0.05;
const SUPPORT_URL = "https://fantasydownload.com/support";
const PRIVACY_URL = "https://fantasydownload.com/privacy";

function getInitialMobileRankingsZoomIndex() {
  return MOBILE_RANKINGS_ZOOM_LEVELS.indexOf(1);
}

function getInitialMobileBoardZoom() {
  return 1;
}

function clampMobileSheetHeight(height: number, minHeight: number, maxHeight: number) {
  return Math.min(Math.max(height, minHeight), maxHeight);
}

function getMobileSheetHeightRatio(height: number, minHeight: number, maxHeight: number) {
  const range = maxHeight - minHeight;
  if (range <= 0) return 0;
  return Math.min(1, Math.max(0, (height - minHeight) / range));
}

function getMobileSheetHeightForRatio(ratio: number, minHeight: number, maxHeight: number) {
  const clampedRatio = Math.min(1, Math.max(0, ratio));
  return minHeight + (maxHeight - minHeight) * clampedRatio;
}

type MobileSourceTabVisibility = Record<RankingsListKey, boolean>;

function getMobileRankingsSourceLabel(key: RankingsListKey) {
  if (key === "Experts") return "Expert Rankings";
  return "My Rankings";
}

function isNativeIosShell() {
  if (typeof window === "undefined") return false;
  const capacitor = (window as any).Capacitor;
  if (capacitor?.isNativePlatform?.()) return true;
  return window.location.protocol === "capacitor:";
}

function getTouchTabLabel(tab: BoardTab, singleLine: boolean) {
  if (tab === "Teams") return "Rosters";
  if (singleLine) return tab;
  if (tab === "Rankings Board") return <>Rankings<br />Board</>;
  if (tab === "Draft Board") return <>Draft<br />Board</>;
  if (tab === "Draft Planner") return <>Draft<br />Planner</>;
  return tab;
}

const mobileSettingsSelectStyle: React.CSSProperties = {
  borderRadius: 10,
  border: "1px solid rgba(255,255,255,0.12)",
  backgroundColor: "rgba(255,255,255,0.06)",
  backgroundImage:
    "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='10' height='6' viewBox='0 0 10 6'%3E%3Cpath d='M1 1l4 4 4-4' fill='none' stroke='white' stroke-width='1.5' stroke-linecap='round' stroke-linejoin='round'/%3E%3C/svg%3E\")",
  backgroundRepeat: "no-repeat",
  backgroundPosition: "right 10px center",
  backgroundSize: "10px 6px",
  color: "var(--text-0)",
  padding: "8px 28px 8px 10px",
  outline: "none",
  appearance: "none",
  WebkitAppearance: "none",
  MozAppearance: "none",
};

function normalizeMobileSearch(value: string) {
  return value
    .toLowerCase()
    .normalize("NFKD")
    .replace(/[^\p{L}\p{N}\s]/gu, "")
    .replace(/\s+/g, " ")
    .trim();
}

type MobileDraftCompanionViewProps = {
  favoriteIds: Set<string>;
  plannerRoundById: Record<string, number>;
  setPlannerRoundById: React.Dispatch<React.SetStateAction<Record<string, number>>>;
  plannerOrderedIds: string[];
  setPlannerOrderedIds: React.Dispatch<React.SetStateAction<string[]>>;
  boardTab: BoardTab;
  setBoardTab: (tab: BoardTab) => void;
  rounds: number;
  setRounds: React.Dispatch<React.SetStateAction<number>>;
  teams: number;
  setTeams: React.Dispatch<React.SetStateAction<number>>;
  onAddPlayer: (name: string, position: Position) => void;
  draftStyle: DraftStyle;
  setDraftStyle: (style: DraftStyle) => void;
  rankingsListKey: RankingsListKey;
  setRankingsListKey: React.Dispatch<React.SetStateAction<RankingsListKey>>;
  rankingIds: string[];
  rankingsRankingIds: string[];
  playersById: Record<string, Player>;
  tiersByPos: TiersByPos;
  onUpdateTiersByPos: (pos: Position, tierBreaks: string[]) => void;
  draftedIds: Set<string>;
  onToggleDrafted: (id: string) => void;
  clearAllDrafted: () => void;
  teamNames: string[];
  setTeamNames: React.Dispatch<React.SetStateAction<string[]>>;
  draftSlots: (string | null)[];
  draftSlotTeamIndexes?: Array<number | null>;
  rosterStarterConfig: StarterConfig;
  setRosterStarterConfig: React.Dispatch<React.SetStateAction<StarterConfig>>;
  rosterTeamOverrides: TeamOverridesState;
  setRosterTeamOverrides: React.Dispatch<React.SetStateAction<TeamOverridesState>>;
  onAssignPlayerToDraftSlot: (slotIndex: number, playerId: string) => void;
  posColor: (pos: Position) => string;
  sensors: any;
  onBoardDragEnd: (event: DragEndEvent) => void;
  onDraftBoardDragEnd: (event: DragEndEvent) => void;
  onMoveRankings: (fromIndex: number, toIndex: number) => void;
  onImportRankings: () => void;
  onShareRankings: () => void;
  onExportCheatsheetPdf: () => void;
  onImportExpertRankings: () => void;
  onRemoveExpertRankings: () => void;
  onSetAsRankings?: () => void;
  hideKickers: boolean;
  setHideKickers: React.Dispatch<React.SetStateAction<boolean>>;
  hideDefenses: boolean;
  setHideDefenses: React.Dispatch<React.SetStateAction<boolean>>;
  onToggleFavorite: (id: string) => void;
  onRemoveAddedPlayer: (id: string) => void;
  expertRankingIds: string[];
  onOpenDraftSync?: () => void;
  onRefreshDraftSync?: () => void;
  isRefreshingDraftSync?: boolean;
};

const MobileRankingRow = React.memo(function MobileRankingRow(props: {
  id: string;
  player: Player;
  index: number;
  rank: number;
  drafted: boolean;
  sortable: boolean;
  tabletMode: boolean;
  favorite: boolean;
  posColor: (pos: Position) => string;
  onToggleDrafted: (id: string) => void;
  onToggleFavorite: (id: string) => void;
  onOpenProfile: (id: string) => void;
  onRemoveAddedPlayer: (id: string) => void;
  metricValue?: string;
  tierLabel?: string;
  tierAccent?: string;
  rowZoom: number;
}) {
  const { id, index, rank, player, drafted, sortable, tabletMode, favorite, posColor, onToggleDrafted, onToggleFavorite, onOpenProfile, onRemoveAddedPlayer, metricValue, tierLabel, tierAccent, rowZoom } = props;
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id,
    disabled: !sortable,
  });
  const verticalTransform = transform ? { ...transform, x: 0 } : null;
  const accentGreen = "rgb(34, 197, 94)";
  const baseRowBg = index % 2 === 0 ? "rgba(0,0,0,0.78)" : "rgba(35, 35, 35, 0.86)";
  const draftedRowBg = "rgba(34, 197, 94, 0.16)";
  const rowBg = drafted ? draftedRowBg : baseRowBg;
  const removable = id.startsWith("custom-");
  const density = Math.max(0.1, Math.min(1.15, rowZoom));
  const size = (tabletValue: number, phoneValue: number, minValue: number) =>
    Math.max(minValue, Math.round((tabletMode ? tabletValue : phoneValue) * density));
  const draftCol = size(50, 42, 24);
  const rankCol = size(34, 26, 18);
  const metricCol = size(64, 54, 34);
  const dragCol = size(38, 32, 24);
  const rowGap = size(12, 9, 4);
  const rowPaddingY = size(10, 6, 2);
  const rowPaddingX = size(14, 10, 5);
  const draftButtonHeight = size(30, 24, 16);
  const headshotSize = size(38, 30, 14);
  const playerGap = size(12, 9, 4);
  const nameFontSize = size(18, 14, 8);
  const metaFontSize = size(13, 11, 7);
  const metaDotSize = size(9, 7, 5);
  const teamLogoSize = size(18, 15, 9);
  const metricFontSize = size(13, 11, 7);
  const dragButtonWidth = size(58, 50, 28);
  const dragButtonHeight = size(52, 44, 24);
  const dragIconSize = size(16, 14, 9);

  return (
    <div
      ref={setNodeRef}
      style={{
        boxSizing: "border-box",
        width: "100%",
        minWidth: 0,
        transform: CSS.Transform.toString(verticalTransform),
        transition,
        opacity: isDragging ? 0.88 : 1,
        position: "relative",
        overflow: "visible",
        display: "grid",
        gridTemplateColumns: metricValue
          ? `${draftCol}px ${rankCol}px minmax(0, 1fr) ${metricCol}px ${dragCol}px`
          : `${draftCol}px ${rankCol}px minmax(0, 1fr) ${dragCol}px`,
        alignItems: "center",
        gap: rowGap,
        padding: `${rowPaddingY}px ${rowPaddingX}px`,
        borderBottom: "1px solid rgba(255,255,255,0.08)",
        background: rowBg,
        WebkitUserSelect: sortable ? "none" : undefined,
        userSelect: sortable ? "none" : undefined,
        WebkitTouchCallout: sortable ? "none" : undefined,
      }}
    >
      {tierLabel ? <TierOverlay label={tierLabel} accentColor={tierAccent} /> : null}
      <button
        type="button"
        onClick={() => onToggleDrafted(id)}
        aria-label={drafted ? "Undraft player" : "Draft player"}
        style={{
          height: draftButtonHeight,
          border: "1px solid rgba(255,255,255,0.12)",
          borderRadius: 999,
          background: drafted ? "rgba(239, 68, 68, 0.18)" : "rgba(34, 197, 94, 0.18)",
          color: drafted ? "rgb(239, 68, 68)" : accentGreen,
          fontSize: size(16, 14, 8),
          fontWeight: 900,
          lineHeight: 1,
          cursor: "pointer",
        }}
      >
        {drafted ? "−" : "+"}
      </button>

      <div
        style={{
          color: "rgba(255,255,255,0.72)",
          fontSize: size(16, 13, 8),
          fontWeight: 900,
          textAlign: "center",
          lineHeight: 1,
        }}
      >
        {rank}
      </div>

      <div style={{ minWidth: 0, display: "flex", alignItems: "center", gap: playerGap }}>
        <img
          src={player.imageUrl || "/headshot-placeholder.svg"}
          alt={player.name}
          style={{
            width: headshotSize,
            height: headshotSize,
            borderRadius: 999,
            objectFit: "cover",
            border: "1px solid rgba(255,255,255,0.12)",
            background: "rgba(255,255,255,0.28)",
            flex: "0 0 auto",
          }}
          onError={(event) => {
            const img = event.currentTarget;
            if (!img.src.includes("/headshot-placeholder.svg")) {
              img.src = "/headshot-placeholder.svg";
            }
          }}
        />
        <div style={{ minWidth: 0, flex: 1 }}>
          <div
            style={{
              color: "var(--text-0)",
              fontSize: nameFontSize,
              fontWeight: 900,
              lineHeight: 1.1,
              opacity: drafted ? 0.82 : 1,
              display: "flex",
              alignItems: "center",
              gap: size(4, 3, 2),
              minWidth: 0,
            }}
          >
            <div
              style={{
                minWidth: 0,
                flex: 1,
                display: "flex",
                alignItems: "center",
                gap: size(4, 3, 2),
              }}
            >
              <button
                type="button"
                onClick={() => onOpenProfile(id)}
                style={{
                  minWidth: 0,
                  maxWidth: "100%",
                  flex: "0 1 auto",
                  border: "none",
                  background: "transparent",
                  padding: 0,
                  margin: 0,
                  color: "inherit",
                  font: "inherit",
                  textAlign: "left",
                  cursor: "pointer",
                }}
              >
                <span
                  style={{
                    display: "block",
                    minWidth: 0,
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                  }}
                >
                  {player.name}
                </span>
              </button>
              {removable ? (
                <button
                  type="button"
                  onClick={() => onRemoveAddedPlayer(id)}
                  aria-label={`Remove ${player.name}`}
                  title={`Remove ${player.name}`}
                  style={{
                    flex: "0 0 auto",
                    width: size(22, 16, 10),
                    height: size(22, 16, 10),
                    marginLeft: tabletMode ? Math.max(0, Math.round(1 * density)) : 0,
                    borderRadius: 999,
                    border: tabletMode ? "1.5px solid rgba(255,255,255,0.42)" : "1px solid rgba(255,255,255,0.3)",
                    background: tabletMode ? "rgba(255,255,255,0.14)" : "rgba(255,255,255,0.08)",
                    color: "rgba(255,255,255,0.98)",
                    fontSize: size(13, 10, 7),
                    fontWeight: 900,
                    lineHeight: 1,
                    padding: 0,
                    display: "inline-flex",
                    alignItems: "center",
                    justifyContent: "center",
                    cursor: "pointer",
                  }}
                >
                  ×
                </button>
              ) : null}
            </div>
            <button
              type="button"
              onClick={() => onToggleFavorite(id)}
              aria-label={favorite ? "Unfavorite player" : "Favorite player"}
              title={favorite ? "Unfavorite" : "Favorite"}
              style={{
                flex: "0 0 auto",
                border: "none",
                background: "transparent",
                color: favorite ? "#fbbf24" : "rgba(255,255,255,0.38)",
                fontSize: size(12, 11, 8),
                lineHeight: 1,
                padding: 0,
                cursor: "pointer",
                textShadow: favorite ? "0 1px 2px rgba(0,0,0,0.35)" : "none",
              }}
            >
              ★
            </button>
          </div>
          <div style={{ marginTop: size(3, 2, 0), display: "flex", alignItems: "center", gap: size(8, 7, 3), color: "rgba(255,255,255,0.68)", fontSize: metaFontSize, fontWeight: 700 }}>
            <span
              style={{
                width: metaDotSize,
                height: metaDotSize,
                borderRadius: 999,
                background: posColor(player.position),
                flex: "0 0 auto",
              }}
            />
            <span>{player.position}</span>
            <TeamLogo
              team={player.team}
              size={teamLogoSize}
              fallback={<span>{formatTeamAbbreviation(player.team, "FA")}</span>}
            />
          </div>
        </div>
      </div>

      {metricValue ? (
        <div
          style={{
            color: "rgba(255,255,255,0.9)",
            fontSize: metricFontSize,
            fontWeight: 800,
            lineHeight: 1.1,
            textAlign: "right",
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
            fontVariantNumeric: "tabular-nums",
          }}
        >
          {metricValue}
        </div>
      ) : null}

      <button
        type="button"
        aria-label={`Press and hold to reorder ${player.name}`}
        {...attributes}
        {...listeners}
        style={{
          width: dragButtonWidth,
          height: dragButtonHeight,
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          position: "relative",
          borderRadius: size(9, 8, 5),
          border: "none",
          background: "transparent",
          color: sortable ? "rgba(255,255,255,0.84)" : "rgba(255,255,255,0.38)",
          cursor: sortable ? "grab" : "default",
          touchAction: sortable ? "none" : "auto",
          WebkitUserSelect: sortable ? "none" : undefined,
          userSelect: sortable ? "none" : undefined,
          WebkitTouchCallout: sortable ? "none" : undefined,
          opacity: sortable ? 1 : 0.65,
          marginRight: -Math.max(3, size(10, 8, 3)),
        }}
      >
        <svg width={dragIconSize} height={dragIconSize} viewBox="0 0 20 20" fill="none" aria-hidden="true">
          <path d="M4 5h12M4 10h12M4 15h12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
        </svg>
        {!sortable ? (
          <svg
            width={size(24, 22, 12)}
            height={size(24, 22, 12)}
            viewBox="0 0 24 24"
            fill="none"
            aria-hidden="true"
            style={{ position: "absolute" }}
          >
            <circle cx="12" cy="12" r="8.5" stroke="currentColor" strokeWidth="1.8" opacity="0.9" />
            <path d="M7.5 16.5l9-9" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
          </svg>
        ) : null}
      </button>
    </div>
  );
});

export default function MobileDraftCompanionView(props: MobileDraftCompanionViewProps) {
  const {
    favoriteIds,
    plannerRoundById,
    setPlannerRoundById,
    plannerOrderedIds,
    setPlannerOrderedIds,
    boardTab,
    setBoardTab,
    rounds,
    setRounds,
    teams,
    setTeams,
    onAddPlayer,
    draftStyle,
    setDraftStyle,
    rankingsListKey,
    setRankingsListKey,
    rankingIds,
    rankingsRankingIds,
    playersById,
    tiersByPos,
    onUpdateTiersByPos,
    draftedIds,
    onToggleDrafted,
    clearAllDrafted,
    teamNames,
    setTeamNames,
    draftSlots,
    rosterStarterConfig,
    setRosterStarterConfig,
    rosterTeamOverrides,
    setRosterTeamOverrides,
    onAssignPlayerToDraftSlot,
    posColor,
    sensors,
    onBoardDragEnd,
    onDraftBoardDragEnd,
    onMoveRankings,
    onImportRankings,
    onShareRankings,
    onExportCheatsheetPdf,
    onImportExpertRankings,
    onRemoveExpertRankings,
    onSetAsRankings,
    hideKickers,
    setHideKickers,
    hideDefenses,
    setHideDefenses,
    onToggleFavorite,
    onRemoveAddedPlayer,
    expertRankingIds,
    onOpenDraftSync,
    onRefreshDraftSync,
    isRefreshingDraftSync = false,
  } = props;

  const baseCollapsedPeekHeight = 58;
  const [viewportWidth, setViewportWidth] = useState<number>(() =>
    typeof window === "undefined" ? 390 : window.innerWidth
  );
  const initialHeight =
    typeof window === "undefined" ? 340 : Math.min(Math.max(window.innerHeight * 0.42, 180), window.innerHeight - 132);
  const [sheetHeight, setSheetHeight] = useState<number>(initialHeight);
  const [query, setQuery] = useState("");
  const [activePosition, setActivePosition] = useState<"ALL" | Position>("ALL");
  const [hideDrafted, setHideDrafted] = useState(() => {
    if (typeof window === "undefined") return false;
    return window.localStorage.getItem("fantasy-board:mobile-hide-drafted") === "true";
  });
  const [rankingsZoomIndex, setRankingsZoomIndex] = useState(getInitialMobileRankingsZoomIndex);
  const [boardZoom, setBoardZoom] = useState(getInitialMobileBoardZoom);
  const [mobileSettingsOpen, setMobileSettingsOpen] = useState(false);
  const [mobileInfoOpen, setMobileInfoOpen] = useState(false);
  const [mobileShareOptionsOpen, setMobileShareOptionsOpen] = useState(false);
  const [mobileVisibilityOpen, setMobileVisibilityOpen] = useState(false);
  const [confirmUndraftAllOpen, setConfirmUndraftAllOpen] = useState(false);
  const [confirmRemoveExpertOpen, setConfirmRemoveExpertOpen] = useState(false);
  const [confirmSetMyRanksOpen, setConfirmSetMyRanksOpen] = useState(false);
  const [mobileCheatsheetViewOpen, setMobileCheatsheetViewOpen] = useState(false);
  const [mobileRostersViewOpen, setMobileRostersViewOpen] = useState(false);
  const [mobileCheatsheetVisiblePositions, setMobileCheatsheetVisiblePositions] = useState<Record<Position, boolean>>(
    DEFAULT_CHEATSHEET_VISIBLE_POSITIONS
  );
  const [mobileSourceTabVisibility, setMobileSourceTabVisibility] = useState<MobileSourceTabVisibility>(() => {
    const defaultVisibility: MobileSourceTabVisibility = { Rankings: true, Experts: true };
    if (typeof window === "undefined") {
      return defaultVisibility;
    }
    const raw = window.localStorage.getItem("fantasy-board:mobile-source-tab-visibility");
    if (!raw) {
      return defaultVisibility;
    }
    try {
      JSON.parse(raw) as Partial<MobileSourceTabVisibility>;
      return {
        Rankings: true,
        Experts: true,
      };
    } catch {
      return defaultVisibility;
    }
  });
  const [profilePlayerId, setProfilePlayerId] = useState<string | null>(null);
  const [newPlayerName, setNewPlayerName] = useState("");
  const [newPlayerPos, setNewPlayerPos] = useState<Position>("RB");
  const [viewportHeight, setViewportHeight] = useState<number>(() =>
    typeof window === "undefined" ? 844 : window.innerHeight
  );
  const dragStateRef = useRef<{ startY: number; startHeight: number; moved: boolean } | null>(null);
  const mobileSettingsRef = useRef<HTMLDivElement | null>(null);
  const mobileVisibilityRef = useRef<HTMLDivElement | null>(null);
  const topTabsRef = useRef<HTMLDivElement | null>(null);
  const [topTabsTop, setTopTabsTop] = useState<number | null>(null);
  const [topTabsBottom, setTopTabsBottom] = useState<number | null>(null);
  const sheetHeightRatioRef = useRef<number | null>(null);
  const sheetBoundsRef = useRef<{ minHeight: number; maxHeight: number } | null>(null);
  const mobileSensors = useSensors(useSensor(PointerSensor, { activationConstraint: { delay: 275, tolerance: 8 } }));
  const isLandscape = viewportWidth > viewportHeight;
  const isTablet = viewportWidth >= 768 && viewportHeight >= 700;
  const collapsedPeekHeight = isLandscape ? 74 : baseCollapsedPeekHeight;
  const tabletUiScale = isTablet ? Math.max(0.88, Math.min(1.08, viewportWidth / 960)) : 1;
  const topTabsSingleLine = isTablet || isLandscape || viewportWidth >= 430;
  const topTabFontSize = isTablet
    ? Math.max(12, Math.min(15, viewportWidth / 76))
    : isLandscape
      ? Math.max(11, Math.min(13, viewportWidth / 78))
      : Math.max(9, Math.min(11, viewportWidth / 39));
  const topTabMinHeight = isTablet
    ? Math.max(38, Math.min(46, viewportHeight / 17))
    : isLandscape
      ? Math.max(34, Math.min(42, viewportHeight / 9))
      : Math.max(30, Math.min(38, viewportHeight / 22));
  const topTabPaddingX = isTablet
    ? Math.max(14, Math.min(22, viewportWidth / 58))
    : isLandscape
      ? Math.max(10, Math.min(18, viewportWidth / 68))
      : Math.max(7, Math.min(11, viewportWidth / 48));
  const topTabPaddingY = isTablet ? 8 : isLandscape ? Math.max(6, Math.min(8, viewportHeight / 54)) : 5;
  const topTabMinWidth = isTablet
    ? Math.max(96, Math.min(132, viewportWidth / 8))
    : isLandscape
      ? Math.max(84, Math.min(128, viewportWidth / 8.2))
      : Math.max(58, Math.min(78, viewportWidth / 5.6));
  const topTabsContainerPadding = isTablet ? 3 : isLandscape ? 3 : 2;
  const topTabsOuterPadding = isTablet ? 4 : isLandscape ? 3 : 1;
  const topIconButtonSize = isTablet
    ? Math.max(36, Math.min(44, viewportWidth / 22))
    : isLandscape
      ? Math.max(34, Math.min(40, viewportHeight / 8.5))
      : 32;
  const rankingsControlFontSize = isLandscape
    ? Math.max(10, Math.min(12, viewportWidth / 90))
    : isTablet
      ? Math.max(12, Math.min(15, viewportWidth / 76))
      : Math.max(10, Math.min(12, viewportWidth / 38));
  const rankingsControlPaddingX = isTablet
    ? Math.max(13, Math.min(20, viewportWidth / 64))
    : isLandscape
      ? Math.max(11, Math.min(17, viewportWidth / 72))
      : Math.max(9, Math.min(12, viewportWidth / 42));
  const rankingsControlPaddingY = isLandscape
    ? Math.max(3, Math.min(5, viewportHeight / 95))
    : isTablet
      ? Math.max(8, Math.min(10, viewportHeight / 86))
      : Math.max(6, Math.min(8, viewportHeight / 98));
  const rankingsControlMinHeight = isLandscape
    ? Math.max(24, Math.min(28, viewportHeight / 14))
    : isTablet
      ? Math.max(36, Math.min(44, viewportHeight / 18))
      : Math.max(30, Math.min(36, viewportHeight / 24));
  const rankingsSourceTabMinWidth = isTablet
    ? Math.max(124, Math.min(170, viewportWidth / 5.8))
    : isLandscape
      ? Math.max(118, Math.min(165, viewportWidth / 5.6))
      : Math.max(92, Math.min(124, viewportWidth / 3.8));
  const rankingsPositionTabMinWidth = isTablet
    ? Math.max(58, Math.min(82, viewportWidth / 14))
    : isLandscape
      ? Math.max(54, Math.min(78, viewportWidth / 15))
      : Math.max(44, Math.min(58, viewportWidth / 8));
  const rankingsIconButtonSize = isLandscape
    ? Math.max(24, Math.min(28, viewportHeight / 14))
    : isTablet
      ? Math.max(34, Math.min(42, viewportWidth / 24))
      : Math.max(30, Math.min(36, viewportWidth / 12));
  const settingsCompact = isLandscape || viewportHeight < 620;
  const settingsOverlayPadding = settingsCompact ? Math.max(6, Math.min(10, viewportHeight / 44)) : isTablet ? 20 : 14;
  const settingsPanelPadding = settingsCompact ? 10 : isTablet ? 14 : 12;
  const settingsPanelGap = settingsCompact ? 8 : 12;
  const settingsControlPadding = settingsCompact ? "6px 8px" : "8px 10px";
  const settingsButtonPadding = settingsCompact ? "8px 10px" : "10px 12px";
  const settingsCardPadding = settingsCompact ? "7px 8px" : "8px 10px";
  const settingsZoomCardPadding = settingsCompact ? "7px 8px" : "9px 10px";
  const settingsInnerGap = settingsCompact ? 6 : 10;
  const settingsSmallGap = settingsCompact ? 5 : 8;
  const settingsStepperSize = settingsCompact ? 24 : 28;
  const settingsCloseSize = settingsCompact ? 24 : 28;
  const settingsSelectStyle = settingsCompact
    ? {
        ...mobileSettingsSelectStyle,
        padding: "6px 26px 6px 8px",
        backgroundPosition: "right 8px center",
      }
    : mobileSettingsSelectStyle;
  const rankingsRowZoom = MOBILE_RANKINGS_ZOOM_LEVELS[rankingsZoomIndex] ?? 1;
  const rankingsZoomLabel = `${Math.round(rankingsRowZoom * 100)}%`;
  const boardZoomLabel = `${Math.round(boardZoom * 100)}%`;
  const nativeTopSafeArea = isNativeIosShell() && !isLandscape
    ? "max(env(safe-area-inset-top), 54px)"
    : "env(safe-area-inset-top)";
  const fixedTopTabsTop = isLandscape ? "calc(env(safe-area-inset-top) + 18px)" : nativeTopSafeArea;
  const topTabsReserveHeight = Math.ceil(Math.max(topTabMinHeight, topIconButtonSize) + topTabsOuterPadding * 2 + 8);
  const topTabsBoardGap = 6;
  const boardTopOffset = topTabsBottom == null
    ? `calc(${fixedTopTabsTop} - ${nativeTopSafeArea} + ${topTabsReserveHeight}px)`
    : `calc(${topTabsBottom}px - ${nativeTopSafeArea})`;
  const maxRankingsSheetHeight = useCallback(() => {
    if (typeof window === "undefined") return collapsedPeekHeight;
    const minHeight = collapsedPeekHeight;
    const lockedTop = topTabsTop ?? 0;
    return Math.max(minHeight + 80, window.innerHeight - lockedTop);
  }, [collapsedPeekHeight, topTabsTop]);
  const canAddPlayer = newPlayerName.trim().length > 0;
  const [expertInfoOpen, setExpertInfoOpen] = useState(false);
  const markBoardTapSuppressed = useCallback((durationMs = 220) => {
    suppressBoardTapUntilRef.current = Date.now() + durationMs;
  }, []);

  const shouldSuppressMobileBoardTap = useCallback(
    () => Date.now() < suppressBoardTapUntilRef.current,
    []
  );

  useEffect(() => {
    if (typeof window === "undefined") return;
    window.localStorage.setItem("fantasy-board:mobile-hide-drafted", String(hideDrafted));
  }, [hideDrafted]);

  useEffect(() => {
    if (typeof window === "undefined") return;
    window.localStorage.setItem("fantasy-board:mobile-source-tab-visibility", JSON.stringify(mobileSourceTabVisibility));
  }, [mobileSourceTabVisibility]);

  useEffect(() => {
    if (rankingsListKey === "Rankings") return;
    if (rankingsListKey === "Experts" && mobileSourceTabVisibility.Experts) return;
    setRankingsListKey("Rankings");
  }, [mobileSourceTabVisibility.Experts, rankingsListKey, setRankingsListKey]);

  useEffect(() => {
    if (boardTab !== "Cheatsheet") setMobileCheatsheetViewOpen(false);
  }, [boardTab]);

  useEffect(() => {
    if (boardTab !== "Teams") setMobileRostersViewOpen(false);
  }, [boardTab]);

  useEffect(() => {
    if (typeof document === "undefined") return;

    const root = document.documentElement;
    const body = document.body;
    const appRoot = document.getElementById("root");
    const previousRootBackground = root.style.background;
    const previousBodyBackground = body.style.background;
    const previousRootOverscrollY = root.style.overscrollBehaviorY;
    const previousBodyOverscrollY = body.style.overscrollBehaviorY;
    const previousRootOverscrollX = root.style.overscrollBehaviorX;
    const previousBodyOverscrollX = body.style.overscrollBehaviorX;
    const previousRootOverflow = root.style.overflow;
    const previousBodyOverflow = body.style.overflow;
    const previousAppRootOverflow = appRoot?.style.overflow;
    const previousRootHeight = root.style.height;
    const previousBodyHeight = body.style.height;
    const previousAppRootHeight = appRoot?.style.height;
    const previousRootPosition = root.style.position;
    const previousBodyPosition = body.style.position;
    const previousRootWidth = root.style.width;
    const previousBodyWidth = body.style.width;

    root.style.background = "#08111d";
    body.style.background = "#08111d";
    root.style.overscrollBehaviorY = "none";
    body.style.overscrollBehaviorY = "none";
    root.style.overscrollBehaviorX = "none";
    body.style.overscrollBehaviorX = "none";
    root.style.overflow = "hidden";
    body.style.overflow = "hidden";
    if (appRoot) appRoot.style.overflow = "hidden";
    root.style.height = "100%";
    body.style.height = "100%";
    if (appRoot) appRoot.style.height = "100%";
    root.style.position = "fixed";
    body.style.position = "fixed";
    root.style.width = "100%";
    body.style.width = "100%";

    return () => {
      root.style.background = previousRootBackground;
      body.style.background = previousBodyBackground;
      root.style.overscrollBehaviorY = previousRootOverscrollY;
      body.style.overscrollBehaviorY = previousBodyOverscrollY;
      root.style.overscrollBehaviorX = previousRootOverscrollX;
      body.style.overscrollBehaviorX = previousBodyOverscrollX;
      root.style.overflow = previousRootOverflow;
      body.style.overflow = previousBodyOverflow;
      if (appRoot) appRoot.style.overflow = previousAppRootOverflow ?? "";
      root.style.height = previousRootHeight;
      body.style.height = previousBodyHeight;
      if (appRoot) appRoot.style.height = previousAppRootHeight ?? "";
      root.style.position = previousRootPosition;
      body.style.position = previousBodyPosition;
      root.style.width = previousRootWidth;
      body.style.width = previousBodyWidth;
    };
  }, []);

  useEffect(() => {
    if (typeof window === "undefined") return undefined;

    const measureTopTabs = () => {
      const tabs = topTabsRef.current;
      if (!tabs) return;
      const rect = tabs.getBoundingClientRect();
      const nextTop = Math.floor(rect.top);
      const nextBottom = Math.ceil(rect.bottom);
      setTopTabsTop((current) => (current === nextTop ? current : nextTop));
      setTopTabsBottom((current) => (current === nextBottom ? current : nextBottom));
    };

    measureTopTabs();
    const rafId = window.requestAnimationFrame(measureTopTabs);
    window.addEventListener("resize", measureTopTabs);

    let observer: ResizeObserver | null = null;
    if (typeof ResizeObserver !== "undefined" && topTabsRef.current) {
      observer = new ResizeObserver(measureTopTabs);
      observer.observe(topTabsRef.current);
    }

    return () => {
      window.cancelAnimationFrame(rafId);
      window.removeEventListener("resize", measureTopTabs);
      observer?.disconnect();
    };
  }, [isLandscape, isTablet, viewportWidth, viewportHeight]);

  const submitAddPlayer = () => {
    const name = newPlayerName.trim();
    if (!name) return;
    onAddPlayer(name, newPlayerPos);
    setNewPlayerName("");
    setMobileSettingsOpen(false);
  };

  const visibleRankingIds = useMemo(
    () => rankingIds.filter((id) => Boolean(playersById[id])),
    [rankingIds, playersById]
  );
  const availablePositionTabs = useMemo(() => {
    const present = new Set<Position>();
    for (const id of visibleRankingIds) {
      const position = playersById[id]?.position;
      if (position) present.add(position);
    }

    const ordered: Array<"ALL" | Position> = ["ALL", "QB", "RB", "WR", "TE"];
    if (present.has("K")) ordered.push("K");
    if (present.has("DST")) ordered.push("DST");
    return ordered;
  }, [visibleRankingIds, playersById]);
  const positionFilteredRankingIds = useMemo(() => {
    if (activePosition === "ALL") return visibleRankingIds;
    return visibleRankingIds.filter((id) => playersById[id]?.position === activePosition);
  }, [activePosition, visibleRankingIds, playersById]);
  const originalIndexById = useMemo(() => {
    const next: Record<string, number> = {};
    positionFilteredRankingIds.forEach((id, index) => {
      next[id] = index;
    });
    return next;
  }, [positionFilteredRankingIds]);
  const filteredRankingIds = useMemo(() => {
    const q = normalizeMobileSearch(query);
    return positionFilteredRankingIds.filter((id) => {
      if (hideDrafted && draftedIds.has(id)) return false;
      const player = playersById[id];
      if (!player) return false;
      if (!q) return true;

      const name = normalizeMobileSearch(player.name ?? "");
      return name.includes(q);
    });
  }, [query, positionFilteredRankingIds, playersById, hideDrafted, draftedIds]);
  const profilePlayer = profilePlayerId ? playersById[profilePlayerId] ?? null : null;
  const rankingsScrollTouchRef = useRef<{ startY: number; moved: boolean } | null>(null);
  const suppressBoardTapUntilRef = useRef(0);
  const visibleMobileSourceTabs = useMemo(
    () => MOBILE_SOURCE_TABS.filter((tab) => tab.key === "Rankings" || mobileSourceTabVisibility[tab.key]),
    [mobileSourceTabVisibility]
  );
  const showMobileTiers = rankingsListKey === "Rankings" && activePosition !== "ALL";
  const mobileTierBreaks = useMemo(() => {
    if (!showMobileTiers) return [];
    return tiersByPos?.[activePosition] ?? [];
  }, [showMobileTiers, activePosition, tiersByPos]);
  const mobileTierNumById = useMemo(() => {
    if (!showMobileTiers) return {} as Record<string, number>;
    const breakSet = new Set(mobileTierBreaks);
    let tier = 1;
    const next: Record<string, number> = {};
    positionFilteredRankingIds.forEach((id) => {
      if (breakSet.has(id)) tier += 1;
      next[id] = tier;
    });
    return next;
  }, [showMobileTiers, mobileTierBreaks, positionFilteredRankingIds]);

  useEffect(() => {
    if (!availablePositionTabs.includes(activePosition)) {
      setActivePosition("ALL");
    }
  }, [activePosition, availablePositionTabs]);

  useEffect(() => {
    const onResize = () => {
      setViewportWidth(window.innerWidth);
      setViewportHeight(window.innerHeight);
    };

    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  useEffect(() => {
    const minHeight = collapsedPeekHeight;
    const maxHeight = maxRankingsSheetHeight();
    const previousBounds = sheetBoundsRef.current;

    setSheetHeight((prev) => {
      const previousRatio = previousBounds
        ? getMobileSheetHeightRatio(prev, previousBounds.minHeight, previousBounds.maxHeight)
        : getMobileSheetHeightRatio(prev, minHeight, maxHeight);
      const ratio = sheetHeightRatioRef.current ?? previousRatio;
      const next = previousBounds
        ? getMobileSheetHeightForRatio(ratio, minHeight, maxHeight)
        : clampMobileSheetHeight(prev, minHeight, maxHeight);
      const clampedNext = clampMobileSheetHeight(next, minHeight, maxHeight);
      sheetHeightRatioRef.current = getMobileSheetHeightRatio(clampedNext, minHeight, maxHeight);
      return clampedNext;
    });

    sheetBoundsRef.current = { minHeight, maxHeight };
  }, [collapsedPeekHeight, maxRankingsSheetHeight]);

  useEffect(() => {
    const onPointerMove = (event: PointerEvent) => {
      const dragState = dragStateRef.current;
      if (!dragState) return;
      const minHeight = collapsedPeekHeight;
      const maxHeight = maxRankingsSheetHeight();
      if (Math.abs(event.clientY - dragState.startY) > 4) {
        dragState.moved = true;
      }
      const nextHeight = dragState.startHeight + (dragState.startY - event.clientY);
      const clampedNextHeight = clampMobileSheetHeight(nextHeight, minHeight, maxHeight);
      sheetHeightRatioRef.current = getMobileSheetHeightRatio(clampedNextHeight, minHeight, maxHeight);
      sheetBoundsRef.current = { minHeight, maxHeight };
      setSheetHeight(clampedNextHeight);
    };

    const onPointerUp = () => {
      if (dragStateRef.current) {
        markBoardTapSuppressed(dragStateRef.current.moved ? 420 : 240);
      }
      dragStateRef.current = null;
    };

    window.addEventListener("pointermove", onPointerMove);
    window.addEventListener("pointerup", onPointerUp);
    return () => {
      window.removeEventListener("pointermove", onPointerMove);
      window.removeEventListener("pointerup", onPointerUp);
    };
  }, [collapsedPeekHeight, markBoardTapSuppressed, maxRankingsSheetHeight]);

  useEffect(() => {
    if (!mobileVisibilityOpen) return;

    const onPointerDown = (event: PointerEvent) => {
      const target = event.target as Node | null;
      if (!target) return;
      if (mobileVisibilityRef.current?.contains(target)) return;
      setMobileVisibilityOpen(false);
    };

    window.addEventListener("pointerdown", onPointerDown);
    return () => window.removeEventListener("pointerdown", onPointerDown);
  }, [mobileVisibilityOpen]);

  useEffect(() => {
    if (!mobileSettingsOpen) {
      setConfirmUndraftAllOpen(false);
      setConfirmRemoveExpertOpen(false);
      setMobileShareOptionsOpen(false);
      setMobileInfoOpen(false);
    }
  }, [mobileSettingsOpen]);

  useEffect(() => {
    if (rankingsListKey === "Rankings") {
      setConfirmSetMyRanksOpen(false);
    }
  }, [rankingsListKey]);

  const onListDragEnd = (event: DragEndEvent) => {
    if (activePosition !== "ALL") return;
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    if (rankingsListKey !== "Rankings") return;

    const fromIndex = rankingsRankingIds.indexOf(String(active.id));
    const toIndex = rankingsRankingIds.indexOf(String(over.id));
    if (fromIndex < 0 || toIndex < 0) return;
    onMoveRankings(fromIndex, toIndex);
  };

  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        width: "100%",
        height: "100%",
        minHeight: "100%",
        maxHeight: "100%",
        overflow: "hidden",
        display: "flex",
        flexDirection: "column",
        gap: 10,
        background: "linear-gradient(180deg, #0d1724 0%, #08111d 20%, #08111d 100%)",
        overscrollBehavior: "none",
        paddingTop: nativeTopSafeArea,
      }}
    >
      <div
        ref={topTabsRef}
        style={{
          position: "fixed",
          top: fixedTopTabsTop,
          left: 0,
          right: 0,
          zIndex: 30,
          display: "flex",
          gap: isLandscape ? 8 : 6,
          alignItems: "center",
          justifyContent: "flex-start",
          padding: topTabsOuterPadding,
          borderRadius: 999,
          margin: isTablet ? (isLandscape ? "2px 12px 0" : "12px 12px 0") : isLandscape ? "2px 1px 0" : "6px 1px 0",
          touchAction: "pan-x",
          overscrollBehavior: "contain",
        }}
      >
        <div
          style={{
            borderRadius: 999,
            border: "1px solid rgba(255,255,255,0.12)",
            background: "rgba(38,38,38,0.92)",
            boxShadow: "0 14px 28px rgba(0,0,0,0.32)",
            display: "inline-flex",
            gap: isLandscape ? 3 : 2,
            alignItems: "center",
            padding: topTabsContainerPadding,
            overflowX: "auto",
            maxWidth: "100%",
            minWidth: 0,
            width: "auto",
            flex: "1 1 auto",
            alignSelf: "flex-start",
            WebkitOverflowScrolling: "touch",
            scrollbarWidth: "none",
            msOverflowStyle: "none",
          }}
        >
          {TOUCH_LAYOUT_TABS.map((tab) => (
            <button
              key={tab}
              type="button"
              onClick={() => setBoardTab(tab)}
              style={{
                padding: `${topTabPaddingY}px ${topTabPaddingX}px`,
                borderRadius: 999,
                border: "1px solid transparent",
                background: boardTab === tab ? "rgba(255,255,255,0.92)" : "transparent",
                color: boardTab === tab ? "#0f172a" : "var(--text-0)",
                fontWeight: 800,
                fontSize: topTabFontSize,
                lineHeight: topTabsSingleLine ? `${Math.round(topTabFontSize + 4)}px` : 1.05,
                cursor: "pointer",
                whiteSpace: topTabsSingleLine ? "nowrap" : "normal",
                flex: topTabsSingleLine ? "1 1 0" : "0 0 auto",
                minWidth: topTabMinWidth,
                minHeight: topTabMinHeight,
                textAlign: "center",
                display: "grid",
                placeItems: "center",
              }}
            >
              {getTouchTabLabel(tab, topTabsSingleLine)}
            </button>
          ))}
        </div>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: isLandscape ? 6 : isTablet ? 6 : 4,
            marginLeft: "auto",
            flex: "0 0 auto",
          }}
        >
          {boardTab === "Cheatsheet" || boardTab === "Teams" ? (
            <button
              type="button"
              onClick={() => {
                if (boardTab === "Cheatsheet") setMobileCheatsheetViewOpen(true);
                if (boardTab === "Teams") setMobileRostersViewOpen(true);
              }}
              style={{
                width: topIconButtonSize,
                height: topIconButtonSize,
                borderRadius: 999,
                border: "1px solid rgba(255,255,255,0.12)",
                background:
                  (boardTab === "Cheatsheet" && mobileCheatsheetViewOpen) || (boardTab === "Teams" && mobileRostersViewOpen)
                    ? "rgba(255,255,255,0.92)"
                    : "rgba(38,38,38,0.92)",
                color:
                  (boardTab === "Cheatsheet" && mobileCheatsheetViewOpen) || (boardTab === "Teams" && mobileRostersViewOpen)
                    ? "#0f172a"
                    : "var(--text-0)",
                cursor: "pointer",
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                boxShadow: "0 14px 28px rgba(0,0,0,0.32)",
                padding: 0,
              }}
              aria-label={boardTab === "Teams" ? "Open rosters view settings" : "Open cheatsheet view settings"}
            >
              <svg width={Math.round(topIconButtonSize * 0.5)} height={Math.round(topIconButtonSize * 0.5)} viewBox="0 0 24 24" fill="none" aria-hidden="true">
                <path
                  d="M2 12s3.5-6 10-6 10 6 10 6-3.5 6-10 6S2 12 2 12Z"
                  stroke="currentColor"
                  strokeWidth="1.8"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <circle cx="12" cy="12" r="3.2" stroke="currentColor" strokeWidth="1.8" />
              </svg>
            </button>
          ) : null}
          <button
            type="button"
            onClick={() => setMobileSettingsOpen((open) => !open)}
            style={{
              width: topIconButtonSize,
              height: topIconButtonSize,
              borderRadius: 999,
              border: "1px solid rgba(255,255,255,0.12)",
              background: "rgba(38,38,38,0.92)",
              color: "var(--text-0)",
              cursor: "pointer",
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              boxShadow: "0 14px 28px rgba(0,0,0,0.32)",
            }}
            aria-label="Open mobile draft settings"
          >
            <svg width={Math.round(topIconButtonSize * 0.53)} height={Math.round(topIconButtonSize * 0.53)} viewBox="0 0 24 24" fill="none" aria-hidden="true">
              <path
                d="M4 7h8M16 7h4M4 17h4M12 17h8"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
              />
              <circle cx="14" cy="7" r="2.2" stroke="currentColor" strokeWidth="2" />
              <circle cx="10" cy="17" r="2.2" stroke="currentColor" strokeWidth="2" />
            </svg>
          </button>
        </div>
      </div>

      {mobileSettingsOpen ? (
        <div
          style={{
            position: "absolute",
            inset: 0,
            zIndex: 40,
            background: "rgba(4,7,18,0.62)",
            backdropFilter: "blur(4px)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: settingsOverlayPadding,
          }}
        >
          <div
            ref={mobileSettingsRef}
            style={{
              boxSizing: "border-box",
              width: isTablet || isLandscape
                ? `min(420px, calc(100vw - ${settingsOverlayPadding * 2}px))`
                : `min(340px, calc(100vw - ${settingsOverlayPadding * 2}px))`,
              maxHeight: `calc(100dvh - ${settingsOverlayPadding * 2}px)`,
              padding: settingsPanelPadding,
              borderRadius: 16,
              border: "1px solid rgba(255,255,255,0.12)",
              background: "rgba(18,18,22,0.98)",
              boxShadow: "0 24px 50px rgba(0,0,0,0.42)",
              display: "flex",
              flexDirection: "column",
              gap: settingsPanelGap,
              overflowY: "auto",
              overscrollBehavior: "contain",
              WebkitOverflowScrolling: "touch",
            }}
            onPointerDown={(event) => event.stopPropagation()}
          >
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: settingsInnerGap }}>
              <div style={{ display: "flex", alignItems: "center", gap: 8, minWidth: 0 }}>
                <div style={{ fontSize: 11, fontWeight: 800, color: "rgba(255,255,255,0.68)", letterSpacing: 0.4, textTransform: "uppercase" }}>
                  Draft Settings
                </div>
                <button
                  type="button"
                  onClick={() => setMobileInfoOpen(true)}
                  aria-label="Open app information"
                  title="App information"
                  style={{
                    width: Math.max(24, settingsCloseSize - 2),
                    height: Math.max(24, settingsCloseSize - 2),
                    borderRadius: 999,
                    border: "1px solid rgba(255,255,255,0.18)",
                    background: "rgba(255,255,255,0.08)",
                    color: "rgba(255,255,255,0.86)",
                    cursor: "pointer",
                    fontSize: 13,
                    fontWeight: 900,
                    lineHeight: 1,
                    display: "inline-flex",
                    alignItems: "center",
                    justifyContent: "center",
                    padding: 0,
                    flex: "0 0 auto",
                  }}
                >
                  i
                </button>
              </div>
              <button
                type="button"
                onClick={() => setMobileSettingsOpen(false)}
                style={{
                  width: settingsCloseSize,
                  height: settingsCloseSize,
                  borderRadius: 999,
                  border: "1px solid rgba(255,255,255,0.12)",
                  background: "rgba(255,255,255,0.06)",
                  color: "var(--text-0)",
                  cursor: "pointer",
                }}
              >
                ×
              </button>
            </div>

            <div style={{ display: "grid", gridTemplateColumns: "minmax(0, 1fr) auto auto", gap: settingsSmallGap, alignItems: "center" }}>
              <input
                value={newPlayerName}
                onChange={(event) => setNewPlayerName(event.target.value)}
                onKeyDown={(event) => {
                  if (event.key === "Enter") submitAddPlayer();
                }}
                placeholder="Add player name"
                style={{
                  borderRadius: 10,
                  border: "1px solid rgba(255,255,255,0.12)",
                  background: "rgba(255,255,255,0.06)",
                  color: "var(--text-0)",
                  padding: settingsControlPadding,
                  outline: "none",
                  minWidth: 0,
                }}
              />
              <select
                value={newPlayerPos}
                onChange={(event) => setNewPlayerPos(event.target.value as Position)}
                style={settingsSelectStyle}
              >
                {(["QB", "RB", "WR", "TE", "K", "DST"] as Position[]).map((position) => (
                  <option key={position} value={position}>
                    {position}
                  </option>
                ))}
              </select>
              <button
                type="button"
                onClick={submitAddPlayer}
                disabled={!canAddPlayer}
                style={{
                  padding: settingsControlPadding,
                  borderRadius: 10,
                  border: "1px solid rgba(255,255,255,0.12)",
                  background: canAddPlayer ? "rgba(255,255,255,0.12)" : "rgba(255,255,255,0.05)",
                  color: "var(--text-0)",
                  fontWeight: 800,
                  fontSize: 12,
                  cursor: canAddPlayer ? "pointer" : "not-allowed",
                }}
              >
                Add
              </button>
            </div>

            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: settingsInnerGap }}>
              <div
                style={{
                  borderRadius: 12,
                  border: "1px solid rgba(255,255,255,0.12)",
                  background: "rgba(255,255,255,0.04)",
                  padding: settingsCardPadding,
                  display: "flex",
                  flexDirection: "column",
                  gap: settingsSmallGap,
                }}
              >
                <span style={{ fontSize: 11, fontWeight: 700, color: "rgba(255,255,255,0.72)", textAlign: "center" }}>Teams</span>
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: settingsSmallGap }}>
                  <button type="button" onClick={() => setTeams((n) => Math.max(2, n - 1))} style={{ borderRadius: 999, border: "1px solid rgba(255,255,255,0.12)", background: "rgba(255,255,255,0.06)", color: "var(--text-0)", width: settingsStepperSize, height: settingsStepperSize }}>−</button>
                  <span style={{ fontWeight: 800, color: "var(--text-0)", minWidth: 18, textAlign: "center" }}>{teams}</span>
                  <button type="button" onClick={() => setTeams((n) => Math.min(20, n + 1))} style={{ borderRadius: 999, border: "1px solid rgba(255,255,255,0.12)", background: "rgba(255,255,255,0.06)", color: "var(--text-0)", width: settingsStepperSize, height: settingsStepperSize }}>+</button>
                </div>
              </div>

              <div
                style={{
                  borderRadius: 12,
                  border: "1px solid rgba(255,255,255,0.12)",
                  background: "rgba(255,255,255,0.04)",
                  padding: settingsCardPadding,
                  display: "flex",
                  flexDirection: "column",
                  gap: settingsSmallGap,
                }}
              >
                <span style={{ fontSize: 11, fontWeight: 700, color: "rgba(255,255,255,0.72)", textAlign: "center" }}>Rounds</span>
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: settingsSmallGap }}>
                  <button type="button" onClick={() => setRounds((n) => Math.max(1, n - 1))} style={{ borderRadius: 999, border: "1px solid rgba(255,255,255,0.12)", background: "rgba(255,255,255,0.06)", color: "var(--text-0)", width: settingsStepperSize, height: settingsStepperSize }}>−</button>
                  <span style={{ fontWeight: 800, color: "var(--text-0)", minWidth: 18, textAlign: "center" }}>{rounds}</span>
                  <button type="button" onClick={() => setRounds((n) => Math.min(40, n + 1))} style={{ borderRadius: 999, border: "1px solid rgba(255,255,255,0.12)", background: "rgba(255,255,255,0.06)", color: "var(--text-0)", width: settingsStepperSize, height: settingsStepperSize }}>+</button>
                </div>
              </div>
            </div>

            <div style={{ display: "flex", flexDirection: "column", gap: settingsCompact ? 4 : 6 }}>
              <span style={{ fontSize: 11, fontWeight: 700, color: "rgba(255,255,255,0.72)" }}>Draft Style</span>
              <select
                value={draftStyle}
                onChange={(event) => setDraftStyle(event.target.value as DraftStyle)}
                style={settingsSelectStyle}
              >
                <option value="Snake Draft">Snake Draft</option>
                <option value="Regular Draft">Regular Draft</option>
                <option value="Third Round Reversal">Third Round Reversal</option>
              </select>
            </div>

            <div
              style={{
                borderRadius: 12,
                border: "1px solid rgba(255,255,255,0.12)",
                background: "rgba(255,255,255,0.04)",
                padding: settingsZoomCardPadding,
                display: "grid",
                gap: settingsSmallGap,
              }}
            >
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10 }}>
                <span style={{ fontSize: 11, fontWeight: 700, color: "rgba(255,255,255,0.72)" }}>Board Zoom</span>
                <span style={{ fontSize: 12, fontWeight: 900, color: "var(--text-0)", minWidth: 42, textAlign: "right" }}>
                  {boardZoomLabel}
                </span>
              </div>
              <input
                type="range"
                min={MOBILE_BOARD_ZOOM_MIN}
                max={MOBILE_BOARD_ZOOM_MAX}
                step={MOBILE_BOARD_ZOOM_STEP}
                value={boardZoom}
                aria-label="Board zoom"
                onChange={(event) => {
                  const next = Number(event.target.value);
                  if (Number.isFinite(next)) {
                    setBoardZoom(Math.min(MOBILE_BOARD_ZOOM_MAX, Math.max(MOBILE_BOARD_ZOOM_MIN, next)));
                  }
                }}
                style={{
                  width: "100%",
                  accentColor: "#fff",
                }}
              />
              <div
                aria-hidden="true"
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  color: "rgba(255,255,255,0.52)",
                  fontSize: 10,
                  fontWeight: 800,
                }}
              >
                <span>75%</span>
                <span>150%</span>
              </div>
            </div>

            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: settingsInnerGap }}>
              <button
                type="button"
                onClick={onImportRankings}
                style={{
                  padding: settingsButtonPadding,
                  borderRadius: 12,
                  border: "1px solid rgba(255,255,255,0.12)",
                  background: "rgba(255,255,255,0.08)",
                  color: "var(--text-0)",
                  cursor: "pointer",
                  fontWeight: 800,
                  fontSize: 13,
                }}
              >
                Import
              </button>
              <button
                type="button"
                onClick={() => setMobileShareOptionsOpen(true)}
                style={{
                  padding: settingsButtonPadding,
                  borderRadius: 12,
                  border: "1px solid rgba(255,255,255,0.12)",
                  background: "rgba(255,255,255,0.06)",
                  color: "var(--text-0)",
                  cursor: "pointer",
                  fontWeight: 800,
                  fontSize: 13,
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: 7,
                }}
              >
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                  <path
                    d="M12 15V3m0 0 4 4m-4-4-4 4M6 10v9h12v-9"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
                Share
              </button>
            </div>

            <div style={{ display: "flex", gap: settingsInnerGap, flexWrap: "wrap", justifyContent: "center" }}>
              <button
                type="button"
                onClick={() => setConfirmUndraftAllOpen(true)}
                style={{
                  padding: settingsButtonPadding,
                  borderRadius: 12,
                  border: "1px solid rgba(239,68,68,0.24)",
                  background: "rgba(239,68,68,0.12)",
                  color: "var(--text-0)",
                  cursor: "pointer",
                  fontWeight: 800,
                  fontSize: 13,
                }}
              >
                Restart Draft
              </button>
              {expertRankingIds.length > 0 ? (
                <button
                  type="button"
                  onClick={() => setConfirmRemoveExpertOpen(true)}
                  style={{
                    padding: settingsButtonPadding,
                    borderRadius: 12,
                    border: "1px solid rgba(239,68,68,0.24)",
                    background: "rgba(239,68,68,0.12)",
                    color: "var(--text-0)",
                    cursor: "pointer",
                    fontWeight: 800,
                    fontSize: 13,
                  }}
                >
                  Remove Expert
                </button>
              ) : null}
            </div>

            {onOpenDraftSync ? (
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: onRefreshDraftSync ? "minmax(0, 1fr) auto" : "1fr",
                  gap: settingsInnerGap,
                  marginTop: settingsCompact ? 0 : 4,
                }}
              >
                <button
                  type="button"
                  onClick={() => {
                    setMobileSettingsOpen(false);
                    onOpenDraftSync();
                  }}
                  style={{
                    padding: settingsButtonPadding,
                    borderRadius: 12,
                    border: "1px solid rgba(96,165,250,0.28)",
                    background: "rgba(96,165,250,0.14)",
                    color: "var(--text-0)",
                    cursor: "pointer",
                    fontWeight: 800,
                    fontSize: 13,
                    width: "100%",
                  }}
                >
                  Sync Sleeper Draft
                </button>
                {onRefreshDraftSync ? (
                  <button
                    type="button"
                    onClick={() => {
                      setMobileSettingsOpen(false);
                      onRefreshDraftSync();
                    }}
                    disabled={isRefreshingDraftSync}
                    aria-label="Refresh Sleeper draft sync"
                    style={{
                      width: settingsCompact ? 36 : 44,
                      minWidth: settingsCompact ? 36 : 44,
                      borderRadius: 12,
                      border: "1px solid rgba(96,165,250,0.28)",
                      background: isRefreshingDraftSync ? "rgba(96,165,250,0.08)" : "rgba(96,165,250,0.14)",
                      color: "var(--text-0)",
                      cursor: isRefreshingDraftSync ? "progress" : "pointer",
                      fontWeight: 900,
                      fontSize: 18,
                      display: "inline-flex",
                      alignItems: "center",
                      justifyContent: "center",
                      opacity: isRefreshingDraftSync ? 0.72 : 1,
                    }}
                  >
                    ↻
                  </button>
                ) : null}
              </div>
            ) : null}
          </div>
          {mobileShareOptionsOpen ? (
            <div
              style={{
                position: "absolute",
                inset: 0,
                zIndex: 2,
                background: "rgba(4,7,18,0.58)",
                backdropFilter: "blur(3px)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                padding: isTablet ? 20 : 14,
              }}
            >
              <div
                style={{
                  width: isTablet ? "min(360px, calc(100vw - 40px))" : "min(300px, calc(100vw - 28px))",
                  padding: isTablet ? 14 : 12,
                  borderRadius: 16,
                  border: "1px solid rgba(255,255,255,0.12)",
                  background: "rgba(18,18,22,0.98)",
                  boxShadow: "0 24px 50px rgba(0,0,0,0.42)",
                  display: "grid",
                  gap: 12,
                }}
                onPointerDown={(event) => event.stopPropagation()}
              >
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10 }}>
                  <div style={{ fontSize: 11, fontWeight: 800, color: "rgba(255,255,255,0.68)", letterSpacing: 0.4, textTransform: "uppercase" }}>
                    Share
                  </div>
                  <button
                    type="button"
                    onClick={() => setMobileShareOptionsOpen(false)}
                    style={{
                      width: 28,
                      height: 28,
                      borderRadius: 999,
                      border: "1px solid rgba(255,255,255,0.12)",
                      background: "rgba(255,255,255,0.06)",
                      color: "var(--text-0)",
                      cursor: "pointer",
                    }}
                  >
                    ×
                  </button>
                </div>
                <div style={{ display: "grid", gap: 10 }}>
                  <button
                    type="button"
                    onClick={() => {
                      setMobileShareOptionsOpen(false);
                      onShareRankings();
                    }}
                    style={{
                      padding: "12px 14px",
                      borderRadius: 12,
                      border: "1px solid rgba(255,255,255,0.12)",
                      background: "rgba(255,255,255,0.08)",
                      color: "var(--text-0)",
                      cursor: "pointer",
                      fontWeight: 800,
                      fontSize: 14,
                      textAlign: "left",
                    }}
                  >
                    Rankings CSV
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setMobileShareOptionsOpen(false);
                      onExportCheatsheetPdf();
                    }}
                    style={{
                      padding: "12px 14px",
                      borderRadius: 12,
                      border: "1px solid rgba(255,255,255,0.12)",
                      background: "rgba(255,255,255,0.08)",
                      color: "var(--text-0)",
                      cursor: "pointer",
                      fontWeight: 800,
                      fontSize: 14,
                      textAlign: "left",
                    }}
                  >
                    Cheatsheet PDF
                  </button>
                </div>
              </div>
            </div>
          ) : null}
          {mobileInfoOpen ? (
            <div
              style={{
                position: "absolute",
                inset: 0,
                zIndex: 3,
                background: "rgba(4,7,18,0.58)",
                backdropFilter: "blur(3px)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                padding: isTablet ? 20 : 14,
              }}
            >
              <div
                style={{
                  width: isTablet ? "min(360px, calc(100vw - 40px))" : "min(300px, calc(100vw - 28px))",
                  padding: isTablet ? 14 : 12,
                  borderRadius: 16,
                  border: "1px solid rgba(255,255,255,0.12)",
                  background: "rgba(18,18,22,0.98)",
                  boxShadow: "0 24px 50px rgba(0,0,0,0.42)",
                  display: "grid",
                  gap: 12,
                }}
                onPointerDown={(event) => event.stopPropagation()}
              >
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10 }}>
                  <div>
                    <div style={{ fontSize: 11, fontWeight: 800, color: "rgba(255,255,255,0.68)", letterSpacing: 0.4, textTransform: "uppercase" }}>
                      Fantasy Draft Room
                    </div>
                    <div style={{ marginTop: 3, fontSize: 12, fontWeight: 700, color: "rgba(255,255,255,0.5)" }}>
                      Version 1.0
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() => setMobileInfoOpen(false)}
                    aria-label="Close app information"
                    style={{
                      width: 28,
                      height: 28,
                      borderRadius: 999,
                      border: "1px solid rgba(255,255,255,0.12)",
                      background: "rgba(255,255,255,0.06)",
                      color: "var(--text-0)",
                      cursor: "pointer",
                    }}
                  >
                    ×
                  </button>
                </div>
                <div style={{ display: "grid", gap: 10 }}>
                  <a
                    href={SUPPORT_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{
                      padding: "12px 14px",
                      borderRadius: 12,
                      border: "1px solid rgba(255,255,255,0.12)",
                      background: "rgba(255,255,255,0.08)",
                      color: "var(--text-0)",
                      fontWeight: 800,
                      fontSize: 14,
                      textDecoration: "none",
                    }}
                  >
                    Support
                  </a>
                  <a
                    href={PRIVACY_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{
                      padding: "12px 14px",
                      borderRadius: 12,
                      border: "1px solid rgba(255,255,255,0.12)",
                      background: "rgba(255,255,255,0.08)",
                      color: "var(--text-0)",
                      fontWeight: 800,
                      fontSize: 14,
                      textDecoration: "none",
                    }}
                  >
                    Privacy Policy
                  </a>
                </div>
              </div>
            </div>
          ) : null}
        </div>
      ) : null}

      {mobileVisibilityOpen ? (
        <div
          style={{
            position: "absolute",
            inset: 0,
            zIndex: 39,
            background: "rgba(4,7,18,0.46)",
            backdropFilter: "blur(3px)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: isTablet ? 20 : 14,
          }}
          onPointerDown={() => setMobileVisibilityOpen(false)}
        >
          <div
            ref={mobileVisibilityRef}
            style={{
              width: isTablet ? 360 : 320,
              maxWidth: "calc(100vw - 28px)",
              borderRadius: 16,
              border: "1px solid rgba(255,255,255,0.12)",
              background: "rgba(12,18,34,0.98)",
              boxShadow: "0 18px 38px rgba(0,0,0,0.38)",
              padding: isTablet ? 14 : 12,
              display: "grid",
              gap: isTablet ? 12 : 10,
            }}
            onPointerDown={(event) => event.stopPropagation()}
          >
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10 }}>
              <div style={{ fontSize: 11, fontWeight: 800, color: "rgba(255,255,255,0.68)", letterSpacing: 0.4, textTransform: "uppercase" }}>
                View
              </div>
              <button
                type="button"
                onClick={() => setMobileVisibilityOpen(false)}
                style={{
                  width: 28,
                  height: 28,
                  borderRadius: 999,
                  border: "1px solid rgba(255,255,255,0.12)",
                  background: "rgba(255,255,255,0.06)",
                  color: "var(--text-0)",
                  cursor: "pointer",
                }}
              >
                ×
              </button>
            </div>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 12,
                padding: isTablet ? "8px 10px" : "7px 9px",
                borderRadius: 12,
                border: "1px solid rgba(255,255,255,0.12)",
                background: "rgba(255,255,255,0.05)",
                color: "var(--text-0)",
              }}
            >
              <span style={{ fontSize: isTablet ? 13 : 12, fontWeight: 800 }}>Rankings Zoom</span>
              <div
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 6,
                  padding: 3,
                  borderRadius: 999,
                  border: "1px solid rgba(255,255,255,0.10)",
                  background: "rgba(255,255,255,0.05)",
                }}
              >
                <button
                  type="button"
                  onClick={() => setRankingsZoomIndex((current) => Math.max(0, current - 1))}
                  disabled={rankingsZoomIndex <= 0}
                  aria-label="Zoom rankings out"
                  style={{
                    width: isTablet ? 26 : 24,
                    height: isTablet ? 26 : 24,
                    borderRadius: 999,
                    border: "none",
                    background: "rgba(255,255,255,0.10)",
                    color: "var(--text-0)",
                    cursor: rankingsZoomIndex <= 0 ? "not-allowed" : "pointer",
                    opacity: rankingsZoomIndex <= 0 ? 0.4 : 1,
                    fontSize: isTablet ? 15 : 14,
                    fontWeight: 900,
                    lineHeight: 1,
                  }}
                >
                  -
                </button>
                <span
                  style={{
                    minWidth: 42,
                    textAlign: "center",
                    color: "rgba(255,255,255,0.82)",
                    fontSize: isTablet ? 12 : 11,
                    fontWeight: 900,
                    fontVariantNumeric: "tabular-nums",
                  }}
                >
                  {rankingsZoomLabel}
                </span>
                <button
                  type="button"
                  onClick={() => setRankingsZoomIndex((current) => Math.min(MOBILE_RANKINGS_ZOOM_LEVELS.length - 1, current + 1))}
                  disabled={rankingsZoomIndex >= MOBILE_RANKINGS_ZOOM_LEVELS.length - 1}
                  aria-label="Zoom rankings in"
                  style={{
                    width: isTablet ? 26 : 24,
                    height: isTablet ? 26 : 24,
                    borderRadius: 999,
                    border: "none",
                    background: "rgba(255,255,255,0.10)",
                    color: "var(--text-0)",
                    cursor: rankingsZoomIndex >= MOBILE_RANKINGS_ZOOM_LEVELS.length - 1 ? "not-allowed" : "pointer",
                    opacity: rankingsZoomIndex >= MOBILE_RANKINGS_ZOOM_LEVELS.length - 1 ? 0.4 : 1,
                    fontSize: isTablet ? 15 : 14,
                    fontWeight: 900,
                    lineHeight: 1,
                  }}
                >
                  +
                </button>
              </div>
            </div>
            <div style={{ display: "grid", gap: 10 }}>
              {[
                { label: "Drafted", checked: !hideDrafted, onChange: () => setHideDrafted((prev) => !prev) },
                { label: "Kickers", checked: !hideKickers, onChange: () => setHideKickers((prev) => !prev) },
                { label: "Defense", checked: !hideDefenses, onChange: () => setHideDefenses((prev) => !prev) },
                {
                  label: "Expert Rankings",
                  checked: mobileSourceTabVisibility.Experts,
                  onChange: () =>
                    setMobileSourceTabVisibility((prev) => ({
                      ...prev,
                      Experts: !prev.Experts,
                    })),
                },
              ].map((option) => (
                <label
                  key={option.label}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: 12,
                    padding: isTablet ? "8px 10px" : "7px 9px",
                    borderRadius: 12,
                    border: "1px solid rgba(255,255,255,0.12)",
                    background: "rgba(255,255,255,0.05)",
                    color: "var(--text-0)",
                    fontSize: isTablet ? 13 : 12,
                    fontWeight: 800,
                    cursor: "pointer",
                  }}
                >
                  <span>{option.label}</span>
                  <input
                    className="mobileOverlayCheckbox"
                    type="checkbox"
                    checked={option.checked}
                    onChange={option.onChange}
                    style={{
                      width: 16,
                      height: 16,
                      cursor: "pointer",
                    }}
                  />
                </label>
              ))}
            </div>
          </div>
        </div>
      ) : null}

      <div style={{ flex: 1, minHeight: 0, marginTop: boardTopOffset, marginBottom: sheetHeight }}>
        <Board
          allowRankingsReorder={rankingsListKey === "Rankings"}
          favoriteIds={favoriteIds}
          plannerRoundById={plannerRoundById}
          setPlannerRoundById={setPlannerRoundById}
          plannerOrderedIds={plannerOrderedIds}
          setPlannerOrderedIds={setPlannerOrderedIds}
          boardTab={boardTab}
          setBoardTab={setBoardTab}
          rounds={rounds}
          setRounds={setRounds}
          teams={teams}
          setTeams={setTeams}
          onAddPlayer={onAddPlayer}
          draftStyle={draftStyle}
          setDraftStyle={setDraftStyle}
          rankingIds={rankingIds}
          playersById={playersById}
          tiersByPos={tiersByPos}
          onUpdateTiersByPos={onUpdateTiersByPos}
          draftedIds={draftedIds}
          onToggleDrafted={onToggleDrafted}
          clearAllDrafted={clearAllDrafted}
          teamNames={teamNames}
          setTeamNames={setTeamNames}
          draftSlots={draftSlots}
          draftSlotTeamIndexes={props.draftSlotTeamIndexes}
          rosterStarterConfig={rosterStarterConfig}
          setRosterStarterConfig={setRosterStarterConfig}
          rosterTeamOverrides={rosterTeamOverrides}
          setRosterTeamOverrides={setRosterTeamOverrides}
          onAssignPlayerToDraftSlot={onAssignPlayerToDraftSlot}
          posColor={posColor}
          sensors={sensors}
          onBoardDragEnd={onBoardDragEnd}
          onDraftBoardDragEnd={onDraftBoardDragEnd}
          onToggleFavorite={onToggleFavorite}
          hideKickers={hideKickers}
          hideDefenses={hideDefenses}
          mobileCheatsheetViewOpen={mobileCheatsheetViewOpen}
          setMobileCheatsheetViewOpen={setMobileCheatsheetViewOpen}
          mobileCheatsheetVisiblePositions={mobileCheatsheetVisiblePositions}
          setMobileCheatsheetVisiblePositions={setMobileCheatsheetVisiblePositions}
          mobileTeamsViewOpen={mobileRostersViewOpen}
          setMobileTeamsViewOpen={setMobileRostersViewOpen}
          onOpenPlayerProfile={setProfilePlayerId}
          shouldSuppressMobileBoardTap={shouldSuppressMobileBoardTap}
          availableTabs={TOUCH_LAYOUT_TABS}
          mobileMode
          tabletMode={isTablet}
          tabletScale={tabletUiScale}
          boardZoom={boardZoom}
          mobileTopClearance={topTabsBoardGap}
          allowDraftBoardReorder={false}
          showTabSwitcher={false}
        />
      </div>

      <div
          style={{
            position: "fixed",
            left: 0,
            right: 0,
          bottom: 0,
          height: sheetHeight,
          display: "flex",
          flexDirection: "column",
          zIndex: 36,
          borderTopLeftRadius: isTablet ? 30 : 26,
          borderTopRightRadius: isTablet ? 30 : 26,
          border: "1px solid rgba(255,255,255,0.08)",
          background: "linear-gradient(180deg, rgba(38,38,38,0.98) 0%, rgba(18,18,22,0.98) 100%)",
          boxShadow: "0 -24px 50px rgba(0,0,0,0.42)",
          overflow: "hidden",
        }}
      >
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            minHeight: 0,
            width: isTablet ? "calc(100% + 2px)" : "100%",
            height: isTablet ? "calc(100% + 2px)" : "100%",
            zoom: isTablet && !isLandscape ? tabletUiScale : undefined,
            transition: dragStateRef.current ? "none" : "height 180ms ease",
          }}
        >
          <div
            onPointerDown={(event) => {
              event.preventDefault();
              event.stopPropagation();
              markBoardTapSuppressed(240);
              dragStateRef.current = { startY: event.clientY, startHeight: sheetHeight, moved: false };
            }}
            style={{
              padding: isLandscape ? "6px 12px 4px" : isTablet ? "12px 16px 10px" : "8px 12px 6px",
              touchAction: "none",
              cursor: "ns-resize",
              userSelect: "none",
            }}
          >
            <div
              style={{
                width: 46,
                height: isLandscape ? 8 : 5,
                borderRadius: 999,
                background: "rgba(255,255,255,0.28)",
                margin: isLandscape ? "0 auto 5px" : isTablet ? "0 auto 10px" : "0 auto 8px",
              }}
            />
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: isLandscape ? 5 : isTablet ? 8 : 6,
                marginBottom: isLandscape ? 5 : isTablet ? 10 : 8,
              }}
            >
              <div
                style={{
                  display: "flex",
                  gap: isLandscape ? 5 : isTablet ? 8 : 5,
                  overflowX: "auto",
                  paddingBottom: 2,
                  scrollbarWidth: "none",
                  msOverflowStyle: "none",
                  flex: 1,
                  minWidth: 0,
                }}
              >
                {visibleMobileSourceTabs.map(({ key, label }) => {
                  const active = rankingsListKey === key;
                  return (
                    <button
                      key={key}
                      type="button"
                      onClick={() => setRankingsListKey(key)}
                      onPointerDown={(event) => event.stopPropagation()}
                      style={{
                        flex: isTablet || isLandscape ? "1 1 0" : "0 0 auto",
                        minWidth: rankingsSourceTabMinWidth,
                        minHeight: rankingsControlMinHeight,
                        padding: `${rankingsControlPaddingY}px ${rankingsControlPaddingX}px`,
                        borderRadius: 999,
                        border: active ? "none" : "1px solid rgba(255,255,255,0.1)",
                        background: active ? "rgba(255,255,255,0.92)" : "rgba(255,255,255,0.05)",
                        color: active ? "#0f172a" : "var(--text-0)",
                        fontSize: rankingsControlFontSize,
                        lineHeight: `${Math.round(rankingsControlFontSize + 4)}px`,
                        fontWeight: 800,
                        cursor: "pointer",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {label}
                    </button>
                  );
                })}
              </div>
              <button
                type="button"
                onClick={() => setMobileVisibilityOpen((open) => !open)}
                onPointerDown={(event) => event.stopPropagation()}
                style={{
                  flex: "0 0 auto",
                  width: rankingsIconButtonSize,
                  height: rankingsIconButtonSize,
                  borderRadius: 999,
                  border: "1px solid rgba(255,255,255,0.12)",
                  background: mobileVisibilityOpen ? "rgba(255,255,255,0.92)" : "rgba(255,255,255,0.08)",
                  color: mobileVisibilityOpen ? "#0f172a" : "var(--text-0)",
                  cursor: "pointer",
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                  padding: 0,
                }}
                aria-label="Open rankings visibility options"
                >
                  <svg width={Math.round(rankingsIconButtonSize * 0.47)} height={Math.round(rankingsIconButtonSize * 0.47)} viewBox="0 0 24 24" fill="none" aria-hidden="true">
                  <path
                    d="M2 12s3.5-6 10-6 10 6 10 6-3.5 6-10 6S2 12 2 12Z"
                    stroke="currentColor"
                    strokeWidth="1.8"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                  <circle cx="12" cy="12" r="3.2" stroke="currentColor" strokeWidth="1.8" />
                  </svg>
                </button>
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: isLandscape ? 5 : isTablet ? 8 : 6 }}>
              <input
                type="text"
                value={query}
                onChange={(event) => setQuery(event.target.value)}
                placeholder="Search players"
                aria-label="Search players"
                onPointerDown={(event) => event.stopPropagation()}
                style={{
                  flex: 1,
                  minWidth: 0,
                  padding: `${Math.max(5, rankingsControlPaddingY - 1)}px ${rankingsControlPaddingX}px`,
                  borderRadius: isTablet || isLandscape ? 10 : 8,
                  border: "1px solid rgba(255,255,255,0.12)",
                  background: "rgba(255,255,255,0.08)",
                  color: "var(--text-0)",
                  outline: "none",
                  fontSize: Math.max(rankingsControlFontSize, isTablet ? 13 : 11),
                  fontWeight: 700,
                  minHeight: rankingsControlMinHeight,
                }}
              />
              {rankingsListKey !== "Rankings" && onSetAsRankings ? (
                <button
                  type="button"
                  onClick={() => setConfirmSetMyRanksOpen(true)}
                  onPointerDown={(event) => event.stopPropagation()}
                  style={{
                    flex: "0 0 auto",
                    minHeight: rankingsControlMinHeight,
                    padding: `${Math.max(5, rankingsControlPaddingY - 1)}px ${rankingsControlPaddingX}px`,
                    borderRadius: isTablet || isLandscape ? 10 : 8,
                    border: "1px solid rgba(255,255,255,0.12)",
                    background: "rgba(255,255,255,0.92)",
                    color: "#0f172a",
                    outline: "none",
                    fontSize: rankingsControlFontSize,
                    lineHeight: `${Math.round(rankingsControlFontSize + 4)}px`,
                    fontWeight: 900,
                    whiteSpace: "nowrap",
                    cursor: "pointer",
                  }}
                >
                  Set My Ranks
                </button>
              ) : null}
            </div>
            <div
              style={{
                display: "flex",
                gap: isLandscape ? 5 : isTablet ? 8 : 5,
                overflowX: "auto",
                marginTop: isLandscape ? 5 : isTablet ? 10 : 8,
                paddingBottom: 2,
                scrollbarWidth: "none",
                msOverflowStyle: "none",
              }}
            >
              {availablePositionTabs.map((tab) => {
                const active = activePosition === tab;
                return (
                  <button
                    key={tab}
                    type="button"
                    onClick={() => setActivePosition(tab)}
                    style={{
                      flex: isTablet || isLandscape ? "1 1 0" : "0 0 auto",
                      minWidth: rankingsPositionTabMinWidth,
                      minHeight: rankingsControlMinHeight,
                      padding: `${rankingsControlPaddingY}px ${Math.max(10, rankingsControlPaddingX)}px`,
                      borderRadius: 999,
                      border: active ? "none" : "1px solid rgba(255,255,255,0.1)",
                      background: active ? "rgba(255,255,255,0.92)" : "rgba(255,255,255,0.05)",
                      color: active ? "#0f172a" : "var(--text-0)",
                      fontSize: rankingsControlFontSize,
                      lineHeight: `${Math.round(rankingsControlFontSize + 4)}px`,
                      fontWeight: 800,
                      cursor: "pointer",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {tab === "ALL" ? "All" : tab}
                  </button>
                );
              })}
            </div>
          </div>

          <div
            style={{
              flex: 1,
              minHeight: 0,
              minWidth: 0,
              width: "100%",
              maxWidth: "100%",
              overflowY: "auto",
              overflowX: "hidden",
              overscrollBehaviorY: "contain",
              WebkitOverflowScrolling: "touch",
            }}
            onTouchStart={(event) => {
              const touch = event.touches[0];
              if (!touch) return;
              rankingsScrollTouchRef.current = { startY: touch.clientY, moved: false };
            }}
            onTouchMove={(event) => {
              const touch = event.touches[0];
              const current = rankingsScrollTouchRef.current;
              if (!touch || !current) return;
              const scroller = event.currentTarget;
              const deltaY = touch.clientY - current.startY;
              const atTop = scroller.scrollTop <= 0;
              const atBottom = scroller.scrollTop + scroller.clientHeight >= scroller.scrollHeight - 1;
              if ((atTop && deltaY > 0) || (atBottom && deltaY < 0)) {
                event.preventDefault();
              }
              if (Math.abs(touch.clientY - current.startY) > 8) {
                current.moved = true;
              }
            }}
            onTouchEnd={() => {
              if (rankingsScrollTouchRef.current?.moved) {
                markBoardTapSuppressed();
              }
              rankingsScrollTouchRef.current = null;
            }}
            onTouchCancel={() => {
              if (rankingsScrollTouchRef.current?.moved) {
                markBoardTapSuppressed();
              }
              rankingsScrollTouchRef.current = null;
            }}
          >
            {(rankingsListKey === "Experts" && expertRankingIds.length === 0) ||
            (rankingsListKey === "Rankings" && filteredRankingIds.length === 0) ? (
              <div style={{ padding: isTablet ? 18 : 14 }}>
                <div
                  style={{
                    display: "grid",
                    gap: 12,
                    padding: isTablet ? 16 : 14,
                    borderRadius: 18,
                    border: "1px solid rgba(255,255,255,0.10)",
                    background: "rgba(255,255,255,0.05)",
                  }}
                >
                  <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "nowrap", minWidth: 0 }}>
                    <div
                      style={{
                        color: "var(--text-0)",
                        fontSize: isTablet ? 18 : 14,
                        fontWeight: 900,
                        lineHeight: 1.15,
                        minWidth: 0,
                        flex: 1,
                      }}
                    >
                      Import rankings from your favorite fantasy analysts
                    </div>
                    <button
                      type="button"
                      onClick={() => setExpertInfoOpen((open) => !open)}
                      aria-label="Show expert import info"
                      title="Show expert import info"
                      style={{
                        width: isTablet ? 26 : 22,
                        height: isTablet ? 26 : 22,
                        borderRadius: 999,
                        border: "1px solid rgba(255,255,255,0.16)",
                        background: expertInfoOpen ? "rgba(255,255,255,0.14)" : "rgba(255,255,255,0.06)",
                        color: "var(--text-0)",
                        cursor: "pointer",
                        fontWeight: 900,
                        fontSize: isTablet ? 14 : 12,
                        lineHeight: 1,
                        display: "inline-flex",
                        alignItems: "center",
                        justifyContent: "center",
                        padding: 0,
                        flex: "0 0 auto",
                      }}
                    >
                      i
                    </button>
                  </div>
                  <div style={{ display: "flex", justifyContent: "center" }}>
                    <button
                      type="button"
                      onClick={onImportExpertRankings}
                      style={{
                        padding: isTablet ? "9px 14px" : "8px 13px",
                        borderRadius: 999,
                        border: "1px solid rgba(255,255,255,0.12)",
                        background: "rgba(255,255,255,0.92)",
                        color: "#0f172a",
                        cursor: "pointer",
                        fontWeight: 900,
                      }}
                    >
                      Import
                    </button>
                  </div>
                  {expertInfoOpen ? (
                    <div
                      style={{
                        padding: isTablet ? "12px 14px" : "11px 13px",
                        borderRadius: 14,
                        border: "1px solid rgba(255,255,255,0.10)",
                        background: "rgba(255,255,255,0.06)",
                        color: "rgba(255,255,255,0.82)",
                        fontSize: isTablet ? 13 : 12,
                        fontWeight: 700,
                        lineHeight: 1.45,
                      }}
                    >
                      Fantasy experts often offer fantasy rankings in the form of CSV downloads. Download those rankings to your phone and import directly to have a side by side comparison with your favorite analysts.
                    </div>
                  ) : null}
                </div>
              </div>
            ) : (
              <DndContext sensors={mobileSensors} onDragEnd={onListDragEnd}>
                <SortableContext items={filteredRankingIds} strategy={verticalListSortingStrategy}>
                  {filteredRankingIds.map((id, index) => {
                    const player = playersById[id];
                    if (!player) return null;
                    const metricValue = undefined;
                    const visibleOriginalIndex = originalIndexById[id];
                    const previousVisibleOriginalIndex = index === 0 ? -1 : originalIndexById[filteredRankingIds[index - 1]];
                    const showTierHeader =
                      showMobileTiers &&
                      (index === 0 ||
                        mobileTierBreaks.some(
                          (breakId) =>
                            (originalIndexById[breakId] ?? Infinity) > previousVisibleOriginalIndex &&
                            (originalIndexById[breakId] ?? Infinity) <= visibleOriginalIndex
                        ));

                    return (
                      <MobileRankingRow
                        key={id}
                        id={id}
                        index={index}
                        rank={(originalIndexById[id] ?? index) + 1}
                        player={player}
                        drafted={draftedIds.has(id)}
                        sortable={rankingsListKey === "Rankings" && activePosition === "ALL"}
                        tabletMode={isTablet}
                        favorite={favoriteIds.has(id)}
                        posColor={posColor}
                        onToggleDrafted={onToggleDrafted}
                        onToggleFavorite={onToggleFavorite}
                        onOpenProfile={setProfilePlayerId}
                        onRemoveAddedPlayer={onRemoveAddedPlayer}
                        metricValue={metricValue}
                        tierLabel={showTierHeader ? `Tier ${mobileTierNumById[id] ?? 1}` : undefined}
                        tierAccent={activePosition === "ALL" ? undefined : posColor(activePosition)}
                        rowZoom={rankingsRowZoom}
                      />
                    );
                  })}
                </SortableContext>
              </DndContext>
            )}
          </div>
        </div>
      </div>

      {confirmUndraftAllOpen ? (
        <div
          style={{
            position: "absolute",
            inset: 0,
            zIndex: 45,
            background: "rgba(6,8,16,0.72)",
            backdropFilter: "blur(4px)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: isTablet ? 18 : 14,
          }}
          onPointerDown={() => setConfirmUndraftAllOpen(false)}
        >
          <div
            style={{
              width: "100%",
              maxWidth: 280,
              borderRadius: 16,
              border: "1px solid rgba(255,255,255,0.12)",
              background: "rgba(12,18,34,0.98)",
              boxShadow: "0 18px 38px rgba(0,0,0,0.38)",
              padding: isTablet ? 16 : 14,
              display: "grid",
              gap: 12,
            }}
            onPointerDown={(event) => event.stopPropagation()}
          >
            <div style={{ display: "grid", gap: 6 }}>
              <div style={{ fontSize: 14, fontWeight: 900, color: "#fff" }}>Restart Draft?</div>
              <div style={{ fontSize: 12, fontWeight: 700, color: "rgba(255,255,255,0.68)", lineHeight: 1.4 }}>
                This will clear every drafted player from the current board.
              </div>
            </div>
            <div style={{ display: "flex", gap: 8, justifyContent: "flex-end", flexWrap: "wrap" }}>
              <button
                type="button"
                onClick={() => setConfirmUndraftAllOpen(false)}
                style={{
                  padding: isTablet ? "8px 13px" : "7px 10px",
                  borderRadius: 999,
                  border: "1px solid rgba(255,255,255,0.12)",
                  background: "rgba(255,255,255,0.05)",
                  color: "var(--text-0)",
                  fontSize: isTablet ? 12 : 10,
                  fontWeight: 800,
                  cursor: "pointer",
                  whiteSpace: "nowrap",
                }}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={() => {
                  clearAllDrafted();
                  setConfirmUndraftAllOpen(false);
                  setMobileSettingsOpen(false);
                }}
                style={{
                  padding: isTablet ? "8px 13px" : "7px 10px",
                  borderRadius: 999,
                  border: "1px solid rgba(239,68,68,0.24)",
                  background: "rgba(239,68,68,0.12)",
                  color: "var(--text-0)",
                  fontSize: isTablet ? 12 : 10,
                  fontWeight: 800,
                  cursor: "pointer",
                  whiteSpace: "nowrap",
                }}
              >
                Restart Draft
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {confirmRemoveExpertOpen ? (
        <div
          style={{
            position: "absolute",
            inset: 0,
            zIndex: 45,
            background: "rgba(6,8,16,0.72)",
            backdropFilter: "blur(4px)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: isTablet ? 18 : 14,
          }}
          onPointerDown={() => setConfirmRemoveExpertOpen(false)}
        >
          <div
            style={{
              width: "100%",
              maxWidth: 300,
              borderRadius: 16,
              border: "1px solid rgba(255,255,255,0.12)",
              background: "rgba(12,18,34,0.98)",
              boxShadow: "0 18px 38px rgba(0,0,0,0.38)",
              padding: isTablet ? 16 : 14,
              display: "grid",
              gap: 12,
            }}
            onPointerDown={(event) => event.stopPropagation()}
          >
            <div style={{ display: "grid", gap: 6 }}>
              <div style={{ fontSize: 14, fontWeight: 900, color: "#fff" }}>Remove Expert Rankings?</div>
              <div style={{ fontSize: 12, fontWeight: 700, color: "rgba(255,255,255,0.68)", lineHeight: 1.4 }}>
                Are you sure you want to remove imported expert rankings?
              </div>
            </div>
            <div style={{ display: "flex", gap: 8, justifyContent: "flex-end", flexWrap: "wrap" }}>
              <button
                type="button"
                onClick={() => setConfirmRemoveExpertOpen(false)}
                style={{
                  padding: isTablet ? "8px 13px" : "7px 10px",
                  borderRadius: 999,
                  border: "1px solid rgba(255,255,255,0.12)",
                  background: "rgba(255,255,255,0.05)",
                  color: "var(--text-0)",
                  fontSize: isTablet ? 12 : 10,
                  fontWeight: 800,
                  cursor: "pointer",
                  whiteSpace: "nowrap",
                }}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={() => {
                  onRemoveExpertRankings();
                  setConfirmRemoveExpertOpen(false);
                  setMobileSettingsOpen(false);
                }}
                style={{
                  padding: isTablet ? "8px 13px" : "7px 10px",
                  borderRadius: 999,
                  border: "1px solid rgba(239,68,68,0.24)",
                  background: "rgba(239,68,68,0.12)",
                  color: "var(--text-0)",
                  fontSize: isTablet ? 12 : 10,
                  fontWeight: 800,
                  cursor: "pointer",
                  whiteSpace: "nowrap",
                }}
              >
                Remove Expert
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {confirmSetMyRanksOpen ? (
        <div
          style={{
            position: "absolute",
            inset: 0,
            zIndex: 45,
            background: "rgba(6,8,16,0.72)",
            backdropFilter: "blur(4px)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: isTablet ? 18 : 14,
          }}
          onPointerDown={() => setConfirmSetMyRanksOpen(false)}
        >
          <div
            style={{
              width: "100%",
              maxWidth: 320,
              borderRadius: 16,
              border: "1px solid rgba(255,255,255,0.12)",
              background: "rgba(12,18,34,0.98)",
              boxShadow: "0 18px 38px rgba(0,0,0,0.38)",
              padding: isTablet ? 16 : 14,
              display: "grid",
              gap: 12,
            }}
            onPointerDown={(event) => event.stopPropagation()}
          >
            <div style={{ display: "grid", gap: 6 }}>
              <div style={{ fontSize: 14, fontWeight: 900, color: "#fff" }}>Set My Rankings?</div>
              <div style={{ fontSize: 12, fontWeight: 700, color: "rgba(255,255,255,0.68)", lineHeight: 1.4 }}>
                {`Are you sure you want to replacing your current custom rankings with "${getMobileRankingsSourceLabel(rankingsListKey)}"? This will remove all currently set tiers. This action can not be undone.`}
              </div>
            </div>
            <div style={{ display: "flex", gap: 8, justifyContent: "flex-end", flexWrap: "wrap" }}>
              <button
                type="button"
                onClick={() => setConfirmSetMyRanksOpen(false)}
                style={{
                  padding: isTablet ? "8px 13px" : "7px 10px",
                  borderRadius: 999,
                  border: "1px solid rgba(255,255,255,0.12)",
                  background: "rgba(255,255,255,0.05)",
                  color: "var(--text-0)",
                  fontSize: isTablet ? 12 : 10,
                  fontWeight: 800,
                  cursor: "pointer",
                  whiteSpace: "nowrap",
                }}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={() => {
                  onSetAsRankings?.();
                  setConfirmSetMyRanksOpen(false);
                }}
                style={{
                  padding: isTablet ? "8px 13px" : "7px 10px",
                  borderRadius: 999,
                  border: "1px solid rgba(255,255,255,0.12)",
                  background: "rgba(255,255,255,0.92)",
                  color: "#0f172a",
                  fontSize: isTablet ? 12 : 10,
                  fontWeight: 900,
                  cursor: "pointer",
                  whiteSpace: "nowrap",
                }}
              >
                Set My Ranks
              </button>
            </div>
          </div>
        </div>
      ) : null}

      <PlayerProfileModal
        open={Boolean(profilePlayer)}
        onClose={() => setProfilePlayerId(null)}
        player={profilePlayer}
        links={profilePlayer ? playerProfilesById[profilePlayer.id] ?? [] : []}
        gameLogs={profilePlayer ? playerGameLogsById[profilePlayer.id] ?? [] : []}
        rankingIds={rankingIds}
        rankingsRankingIds={rankingsRankingIds}
        expertRankingIds={expertRankingIds}
        playersById={playersById}
        onToggleDrafted={onToggleDrafted}
        draftedIds={draftedIds}
      />
    </div>
  );
}
